import { getMappedSeo, SITE_URL } from '~/composables/useMappedSeo'

const NOINDEX_SEGMENTS = new Set([
  'agendamiento',
  'calendario',
  'carro',
  'checkout',
  'cliente',
  'failed',
  'finish',
  'instalacion',
])

const NOINDEX_ROOTS = new Set([
  'formulario-campania',
  'ganadores-concurso',
])

const LABELS = {
  blog: 'Blog',
  beneficios: 'Beneficios',
  neumaticos: 'Neumáticos',
  baterias: 'Baterías',
  frenos: 'Frenos',
  'nuestros-locales': 'Nuestros locales',
}

const SERVICE_PATHS = new Set([
  '/accesorios',
  '/afinamiento-motor',
  '/alineacion',
  '/amortiguadores',
  '/balanceo',
  '/baterias',
  '/cambio-de-aceite',
  '/electrico-automotriz',
  '/fluidos',
  '/frenos',
  '/geometria-automotriz',
  '/higienizacion',
  '/mantencion-kilometraje',
  '/neumaticos',
  '/revision-tecnica',
  '/tren-delantero',
])

const BRANCHES = [
  ['local-vitacura', 'León Vitacura', 'Av. Vitacura 6390', 'Vitacura', '2 2580 5050'],
  ['local-estoril', 'León Estoril', 'Av. Las Condes 10133', 'Las Condes', '2 2580 5090'],
  ['local-colon', 'León Colón', 'Av. Cristóbal Colón 6640', 'Las Condes', '2 2580 5060'],
  ['local-chicureo', 'León Chicureo', 'Av. Chicureo km 2,8', 'Colina', '2 2580 5029'],
  ['local-diagonal', 'León Plaza Egaña', 'Av. Irarrázaval 4770', 'Ñuñoa', '2 2580 5070'],
  ['local-irarrazaval', 'León Irarrázaval', 'Av. Irarrázaval 1518', 'Ñuñoa', '2 2580 5080'],
  ['local-laflorida', 'León La Florida - Santa Amalia', 'Av. Santa Amalia 1416', 'La Florida', '2 2580 5016'],
  ['local-vicmack', 'León La Florida - Plaza Vespucio', 'Av. Vicuña Mackenna Poniente 6556', 'La Florida', '2 2580 5044'],
  ['local-maipu', 'León Maipú', 'Alberto Llona 1254', 'Maipú', '2 2580 5027'],
  ['local-penalolen', 'León Peñalolén', 'Consistorial 5210', 'Peñalolén', '2 2580 5002'],
  ['local-antofagasta', 'León Antofagasta', 'Simón Bolívar 374', 'Antofagasta', '2 2580 5015'],
  ['local-laserena', 'León La Serena', 'Balmaceda 1450', 'La Serena', '2 2580 5061'],
  ['local-vina', 'León Viña del Mar', 'Quillota 117, esq. 2 Norte', 'Viña del Mar', '2 2580 5025'],
  ['local-rancagua', 'León Rancagua', "L. Bernardo O'Higgins 310", 'Rancagua', '2 2580 5035'],
  ['local-concepcion', 'León Concepción', 'Freire 1', 'Concepción', '2 2580 5045'],
  ['local-puertomontt', 'León Puerto Montt', 'Diego Portales 1330', 'Puerto Montt', '2 2580 5055'],
]

function normalizePath(path) {
  if (!path || path === '/') return '/'
  return `/${path.split('/').filter(Boolean).join('/')}`
}

function labelFor(segment) {
  if (LABELS[segment]) return LABELS[segment]
  return segment
    .replace(/^local-/, 'LEÓN ')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase())
}

function shouldNoindex(path) {
  const segments = path.split('/').filter(Boolean)
  return NOINDEX_ROOTS.has(segments[0]) || segments.some(segment => NOINDEX_SEGMENTS.has(segment))
}

function breadcrumbsFor(path) {
  const segments = path.split('/').filter(Boolean)
  const items = [{
    '@type': 'ListItem',
    position: 1,
    name: 'Inicio',
    item: `${SITE_URL}/`,
  }]

  segments.forEach((segment, index) => {
    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name: labelFor(segment),
      item: `${SITE_URL}/${segments.slice(0, index + 1).join('/')}`,
    })
  })

  return items
}

function webPageSchema(url, title) {
  return {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: title,
    inLanguage: 'es-CL',
  }
}

function breadcrumbSchema(path, url) {
  return {
    '@type': 'BreadcrumbList',
    '@id': `${url}#breadcrumb`,
    itemListElement: breadcrumbsFor(path),
  }
}

function localBusinessSchema(branch) {
  const [slug, name, streetAddress, addressLocality, telephone] = branch
  const url = `${SITE_URL}/nuestros-locales/${slug}`

  return {
    '@type': 'LocalBusiness',
    '@id': `${url}#localbusiness`,
    name,
    url,
    telephone,
    image: `${SITE_URL}/logo-rrss.jpg`,
    parentOrganization: { '@id': `${SITE_URL}/#organization` },
    address: {
      '@type': 'PostalAddress',
      streetAddress,
      addressLocality,
      addressCountry: 'CL',
    },
  }
}

export function buildSeoGraph(path, canonicalPath, mappedSeo) {
  const canonicalUrl = `${SITE_URL}${canonicalPath}`
  const title = mappedSeo?.title || labelFor(path.split('/').filter(Boolean).at(-1) || 'Inicio')

  if (path === '/') {
    return [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'LEÓN Servicio Automotriz',
        url: `${SITE_URL}/`,
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo-rrss.jpg` },
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: 'LEÓN Servicio Automotriz',
        inLanguage: 'es-CL',
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      webPageSchema(canonicalUrl, mappedSeo?.title || 'LEÓN Servicio Automotriz'),
    ]
  }

  if (SERVICE_PATHS.has(path) || (mappedSeo && path !== '/')) {
    return [
      {
        '@type': 'Service',
        '@id': `${canonicalUrl}#service`,
        name: mappedSeo?.h1 || title,
        description: mappedSeo?.description,
        url: canonicalUrl,
        image: `${SITE_URL}/logo-rrss.jpg`,
        provider: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'LEÓN Servicio Automotriz' },
        areaServed: { '@type': 'Country', name: 'Chile' },
      },
      webPageSchema(canonicalUrl, title),
      breadcrumbSchema(canonicalPath, canonicalUrl),
    ]
  }

  if (path === '/nuestros-locales') {
    const businesses = BRANCHES.map(localBusinessSchema)
    return [
      {
        '@type': 'ItemList',
        '@id': `${canonicalUrl}#itemlist`,
        name: 'Locales LEÓN Servicio Automotriz',
        numberOfItems: businesses.length,
        itemListElement: businesses.map((business, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: { '@id': business['@id'] },
        })),
      },
      ...businesses,
    ]
  }

  if (path.startsWith('/nuestros-locales/')) {
    const slug = path.split('/').filter(Boolean)[1]
    const branch = BRANCHES.find(item => item[0] === slug)
    return [
      ...(branch ? [localBusinessSchema(branch)] : []),
      webPageSchema(canonicalUrl, title),
      breadcrumbSchema(canonicalPath, canonicalUrl),
    ]
  }

  if (path === '/blog') {
    return [
      {
        '@type': 'Blog',
        '@id': `${canonicalUrl}#blog`,
        name: 'Blog LEÓN Servicio Automotriz',
        url: canonicalUrl,
        inLanguage: 'es-CL',
        publisher: { '@id': `${SITE_URL}/#organization` },
      },
      webPageSchema(canonicalUrl, 'Blog LEÓN Servicio Automotriz'),
      breadcrumbSchema(canonicalPath, canonicalUrl),
    ]
  }

  return [
    webPageSchema(canonicalUrl, title),
    breadcrumbSchema(canonicalPath, canonicalUrl),
  ]
}

export function useGlobalSeo() {
  const route = useRoute()
  const path = computed(() => normalizePath(route.path))
  const mappedSeo = computed(() => getMappedSeo(path.value))
  const canonicalPath = computed(() => mappedSeo.value?.canonical || path.value)
  const canonicalUrl = computed(() => `${SITE_URL}${canonicalPath.value}`)
  const noindex = computed(() => shouldNoindex(path.value))

  useSeoMeta({
    robots: () => noindex.value
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  })

  useHead(() => {
    const graph = buildSeoGraph(path.value, canonicalPath.value, mappedSeo.value)

    return {
      link: [{ rel: 'canonical', href: canonicalUrl.value }],
      script: [{
        key: 'global-seo-schema',
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@graph': graph,
        }),
      }],
    }
  })
}
